import type {
    ActionCreatorWithPayload,
    ActionCreatorWithoutPayload,
    ActionCreatorWithPreparedPayload,
} from '@reduxjs/toolkit';
import { createAction } from '@reduxjs/toolkit';

import { DEFAULT_META_KEY } from './constants';
import type { Meta } from './types';

/**
 * Bind actionCreator without payload
 */
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export function actionBinder<T extends string>(
    actionCreator: ActionCreatorWithoutPayload<T>,
    id: string,
    predefinedMeta?: Meta,
): ActionCreatorWithPreparedPayload<[], undefined, T, never, Meta>;

/**
 * Bind actionCreator with one parameter
 */
export function actionBinder<T extends string, P>(
    actionCreator: ActionCreatorWithPreparedPayload<[P], P, T>,
    id: string,
    predefinedMeta?: Meta,
): ActionCreatorWithPreparedPayload<[P], P, T, never, Meta>;

/**
 * Bind actionCreator with two parameters. First for payloadPrepare, second for meta
 */
export function actionBinder<T extends string, P, M extends {}, E>(
    actionCreator: ActionCreatorWithPreparedPayload<[P, M], P, T, E, M>,
    id: string,
    predefinedMeta?: Meta,
): ActionCreatorWithPreparedPayload<[P, M], P, T, E, Meta & M>;

/**
 * Bind actionCreator with payload
 */
export function actionBinder<T extends string, P>(
    actionCreator: ActionCreatorWithPayload<P, T>,
    id: string,
    predefinedMeta?: Meta,
): ActionCreatorWithPreparedPayload<[P], P, T, never, Meta>;

/**
 * Bind actionCreator with prepared payload
 */
export function actionBinder<T extends string, A extends unknown[], P>(
    actionCreator: ActionCreatorWithPreparedPayload<A, P>,
    id: string,
    predefinedMeta?: Meta,
): ActionCreatorWithPreparedPayload<A, P, T, never, Meta>;

/**
 * Add multiReducerId in meta of action
 *
 * @param actionCreator - action creator with type property (for example created by createAction or createSlice from @reduxjs/toolkit)
 * @param id - id of reducer in multiReducer
 * @param predefinedMeta - user pre-defined meta (default meta that alway exists in the action)
 *
 * @return action creator as first param but with multiReducerId in meta.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function actionBinder(actionCreator: any, id: string, predefinedMeta: Meta) {
    return createAction(actionCreator.type, (...payload) => {
        const action = actionCreator(...payload);

        return { ...action, meta: { ...predefinedMeta, ...action.meta, [DEFAULT_META_KEY]: id } };
    });
}
